/**
 * The simulator's clouds along a RogerATC flight, as a timeline the vertical
 * profile can draw under the track.
 *
 * The plugin sends X-Plane's three cloud layers on every live report
 * (`LiveSim.cloudLayers`, RogerATC protocol v17). Copying them into every track
 * point would repeat the same sky thousands of times, so the sky is kept as
 * **samples**: one whenever it changed enough to see on a chart, each standing
 * until the next. The live screen builds them in memory (`liveTrack.ts`), the
 * server keeps them in the archive (`archive.ts`, `pluginArchive.ts`), and both
 * read through this one module so they cannot disagree about what "changed"
 * means.
 *
 * These are **simulator clouds** — X-Plane's weather, live or the pilot's own
 * setting — never a METAR, and every surface that draws them says so. Heights
 * are ft MSL, as X-Plane gives them.
 *
 * No sample at all is a plugin that did not send clouds (older than v17), and
 * the chart says "not sent"; a sample with no layers is the simulator saying
 * the sky is clear. The two are never folded together.
 *
 * Pure.
 */

import { OKTAS } from '../weather/cloudProfile'
import type { LiveCloudLayer } from './live'

/** The sky as it stood from `atMs` until the next sample. */
export interface SimCloudSample {
	atMs: number
	/** Lowest first; empty is a clear sky. */
	layers: LiveCloudLayer[]
}

/** Never two samples closer than this: X-Plane's real weather drifts every few seconds. */
export const CLOUD_SAMPLE_MIN_MS = 30_000
/** A base or a top that moved less than this is the same layer on a chart. */
export const CLOUD_CHANGE_FT = 200
/** Coverage that moved less than one okta is the same layer too. */
export const CLOUD_CHANGE_COVERAGE = 1 / 8
/** Samples kept per session: a sky that changes every 30 s for 2.5 hours. */
export const MAX_CLOUD_SAMPLES = 300
/** In memory on the live screen, the same cap as the track it is drawn under. */
export const MAX_LIVE_CLOUD_SAMPLES = 1_000
/** X-Plane's blended `cloud_type`: from this up the layer is cumulonimbus. */
export const CUMULONIMBUS_TYPE = 2.5

/** Oktas a coverage fraction is, 0–8. */
export function oktasOf(coverage: number): number {
	return Math.max(0, Math.min(8, Math.round(coverage * 8)))
}

/**
 * The METAR word for a coverage: FEW, SCT, BKN, OVC by ICAO's oktas — or null
 * below half an okta, which is no layer worth naming.
 */
export function amountOfCoverage(coverage: number): 'FEW' | 'SCT' | 'BKN' | 'OVC' | null {
	const oktas = oktasOf(coverage)
	if (oktas < OKTAS.FEW.min) return null
	if (oktas <= OKTAS.FEW.max) return 'FEW'
	if (oktas <= OKTAS.SCT.max) return 'SCT'
	if (oktas <= OKTAS.BKN.max) return 'BKN'
	return 'OVC'
}

export function isCumulonimbus(layer: Pick<LiveCloudLayer, 'cloudType'>): boolean {
	return layer.cloudType !== null && layer.cloudType >= CUMULONIMBUS_TYPE
}

function layerMoved(a: LiveCloudLayer, b: LiveCloudLayer): boolean {
	if (Math.abs(a.baseFt - b.baseFt) >= CLOUD_CHANGE_FT) return true
	if ((a.topsFt === null) !== (b.topsFt === null)) return true
	if (a.topsFt !== null && b.topsFt !== null && Math.abs(a.topsFt - b.topsFt) >= CLOUD_CHANGE_FT)
		return true
	if (Math.abs(a.coverage - b.coverage) >= CLOUD_CHANGE_COVERAGE) return true
	return isCumulonimbus(a) !== isCumulonimbus(b)
}

/** Whether two skies would look different on the profile. */
export function cloudsChanged(a: readonly LiveCloudLayer[], b: readonly LiveCloudLayer[]): boolean {
	if (a.length !== b.length) return true
	return a.some((layer, index) => layerMoved(layer, b[index]!))
}

/**
 * Whether a report's sky is worth a sample after `last`. The first sky always
 * is; after that only a visible change, and never sooner than
 * `CLOUD_SAMPLE_MIN_MS`. A report that did not carry clouds (`null`) adds
 * nothing — it is not a clear sky.
 */
export function shouldSampleClouds(
	last: SimCloudSample | null,
	atMs: number,
	layers: readonly LiveCloudLayer[] | null,
): boolean {
	if (!layers) return false
	if (!last) return true
	if (atMs - last.atMs < CLOUD_SAMPLE_MIN_MS) return false
	return cloudsChanged(last.layers, layers)
}

/**
 * The samples with this report's sky added when it is due — the same array
 * otherwise, so a caller holding it in a shallow ref redraws only on a change.
 * At the cap the timeline stops growing: the last sky stands, which is the
 * honest reading of "we stopped recording changes".
 */
export function appendCloudSample(
	samples: readonly SimCloudSample[],
	atMs: number,
	layers: readonly LiveCloudLayer[] | null,
	cap = MAX_LIVE_CLOUD_SAMPLES,
): SimCloudSample[] {
	const last = samples[samples.length - 1] ?? null
	if (last && atMs <= last.atMs) return samples as SimCloudSample[]
	if (!shouldSampleClouds(last, atMs, layers) || samples.length >= cap) return samples as SimCloudSample[]
	return [...samples, { atMs, layers: layers!.map((layer) => ({ ...layer })) }]
}

/** The sky that stood at `atMs`: the newest sample at or before it, or null before the first. */
export function cloudsAt(samples: readonly SimCloudSample[], atMs: number): LiveCloudLayer[] | null {
	let found: SimCloudSample | null = null
	for (const sample of samples) {
		if (sample.atMs > atMs) break
		found = sample
	}
	return found ? found.layers : null
}

/**
 * A sample as it sits in DynamoDB: `[atMs, [[baseFt, topsFt|null, coverage, type|null], …]]`
 * — a tuple, like the track points, because a map repeats four names per layer.
 */
export type EncodedCloudSample = [number, [number, number | null, number, number | null][]]

export function encodeCloudSample(sample: SimCloudSample): EncodedCloudSample {
	return [
		sample.atMs,
		sample.layers.map((layer) => [layer.baseFt, layer.topsFt, layer.coverage, layer.cloudType]),
	]
}

function finiteIn(value: unknown, min: number, max: number): number | null {
	return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max ? value : null
}

/** Read a stored sample back; a malformed one is dropped, a malformed layer in it too. */
export function decodeCloudSample(raw: unknown): SimCloudSample | null {
	if (!Array.isArray(raw) || raw.length < 2 || !Array.isArray(raw[1])) return null
	const atMs = finiteIn(raw[0], 0, 8.64e15)
	if (atMs === null) return null
	const layers = (raw[1] as unknown[])
		.map((entry): LiveCloudLayer | null => {
			if (!Array.isArray(entry)) return null
			const baseFt = finiteIn(entry[0], -2_000, 70_000)
			const coverage = finiteIn(entry[2], 0, 1)
			if (baseFt === null || coverage === null) return null
			const topsFt = finiteIn(entry[1], -2_000, 80_000)
			return {
				baseFt,
				topsFt: topsFt !== null && topsFt > baseFt ? topsFt : null,
				coverage,
				cloudType: finiteIn(entry[3], 0, 3),
			}
		})
		.filter((layer): layer is LiveCloudLayer => layer !== null)
		.sort((a, b) => a.baseFt - b.baseFt)
	return { atMs, layers }
}

/** Stored samples, oldest first, malformed ones dropped. */
export function decodeCloudSamples(raw: unknown): SimCloudSample[] {
	if (!Array.isArray(raw)) return []
	return raw
		.map(decodeCloudSample)
		.filter((sample): sample is SimCloudSample => sample !== null)
		.sort((a, b) => a.atMs - b.atMs)
}
